import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { useWouterRouter } from '../../hooks/useWouterNavigation';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { SecurityUtils } from '../../utils/security';
import './LoginPage.css';
import { Input } from '../DesignSystem/primitives/Input';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
    <path d="M1 1h22v22H1z" fill="none" />
  </svg>
);


interface LoginPageProps {
  embedded?: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ embedded = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; general?: string }>({});
  const [touched, setTouched] = useState<{ email?: boolean; password?: boolean }>({});

  const { login, signInWithGoogle, resetPassword, user } = useAuth();
  const { navigate } = useWouterRouter();

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearErrors = () => {
    setErrors({});
  };

  // Auto-redirect si déjà connecté (only when not embedded)
  useEffect(() => {
    if (user && !embedded) {
      navigate('/organizer');
    }
  }, [user, navigate, embedded]);

  const handleBack = () => {
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Marquer tous les champs comme touchés
    setTouched({ email: true, password: true });
    clearErrors();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const sanitizedEmail = SecurityUtils.sanitizeInputForSubmission(email);
      await login(sanitizedEmail, password);
      toast.success('Login successful!', { duration: 3000 });
      navigate('/organizer');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login error';

      // Analyser le message d'erreur pour afficher l'erreur au bon endroit
      if (errorMessage.toLowerCase().includes('email') && !errorMessage.toLowerCase().includes('password')) {
        setErrors({ email: errorMessage });
      } else if (errorMessage.toLowerCase().includes('incorrect') || errorMessage.toLowerCase().includes('invalid')) {
        // Erreur de credentials - afficher sur les deux champs
        setErrors({ general: 'Invalid email or password. Please check your credentials.' });
      } else if (errorMessage.toLowerCase().includes('password') || errorMessage.toLowerCase().includes('mot de passe')) {
        setErrors({ password: errorMessage });
      } else {
        setErrors({ general: errorMessage });
      }

      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      toast.success('Sign in successful!', { duration: 3000 });
      navigate('/organizer');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Connection error';

      // General message for Google auth issues
      if (errorMessage.includes('Google') || errorMessage.includes('OAuth')) {
        toast.error(
          'Google authentication issue. Please try email/password login or use "Forgot Password" with your Google email.',
          { duration: 8000 }
        );
      } else {
        toast.error(errorMessage, { duration: 5000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address', { duration: 10000 });
      return;
    }

    try {
      await resetPassword(email);
      toast.success(
        'Password reset email sent! Check your inbox (including spam folder)',
        { duration: 10000 }
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Reset password error',
        { duration: 10000 }
      );
    }
  };

  return (
    <div className={`login-container ${embedded ? 'login-container--embedded' : ''}`}>
      <div className="login-card">
        {!embedded && (
          <button onClick={handleBack} className="back-button">
            ← Back
          </button>
        )}
        <h2>Sign In</h2>
        <form onSubmit={handleSubmit} className="login-form">
          {/* General error message */}
          {errors.general && (
            <div className="login-error-banner" role="alert">
              <span className="login-error-icon">!</span>
              {errors.general}
            </div>
          )}

          <Input
            id="email"
            name="email"
            type="email"
            label="Email"
            value={email}
            onChange={(val) => {
              setEmail(val);
              if (errors.email || errors.general) {
                clearErrors();
              }
            }}
            error={touched.email ? errors.email : undefined}
            required
          />

          <Input
            id="password"
            name="password"
            type="password"
            label="Password"
            value={password}
            onChange={(val) => {
              setPassword(val);
              if (errors.password || errors.general) {
                clearErrors();
              }
            }}
            error={touched.password ? errors.password : undefined}
            required
          />
          <button
            type="button"
            onClick={handleResetPassword}
            className="forgot-password-button"
          >
            Forgot Password?
          </button>
          <button type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
          <div className="separator">or</div>

          <button 
            type="button"
            onClick={handleGoogleSignIn}
            className="google-button"
            disabled={loading}
          >
            <GoogleIcon />
            Sign in with Google
          </button>

          <div className="auth-links">
            <p>Don't have an account?</p>
            <Link to="/register" className="register-link">
              Create an account
            </Link>
          </div>
        </form>
        <div style={{ 
          fontSize: '13px', 
          color: '#666', 
          textAlign: 'center', 
          marginTop: '15px',
          lineHeight: '1.4'
        }}>
          <strong>Google users:</strong> Use "Forgot Password" with your Google email to set up password login
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

export default LoginPage;